import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import SignupSheet from './components/SignupSheet';
import LandingPage from './components/LandingPage';
import Login from './components/Login';
import CompleteProfile from './components/CompleteProfile';
import UserHeader from './components/UserHeader';
import FeedbackModal from './components/FeedbackModal';

function App() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [profileCheckDone, setProfileCheckDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  useEffect(() => {
    let unsubscribeProfile = null;
    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        // Real-time profile listener
        const docRef = doc(db, 'users', currentUser.uid);
        unsubscribeProfile = onSnapshot(docRef, (docSnap) => {
          if (docSnap.exists()) {
            setUserProfile(docSnap.data());
          } else {
            setUserProfile(null);
          }
          setProfileCheckDone(true);
          setLoading(false);
        });
      } else {
        setUserProfile(null);
        if (unsubscribeProfile) unsubscribeProfile();
        setProfileCheckDone(true);
        setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeProfile) unsubscribeProfile();
    };
  }, []);

  const handleProfileComplete = async () => {
    if (user) {
      const docRef = doc(db, 'users', user.uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setUserProfile(docSnap.data());
      }
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>Loading...</div>;
  }

  // Common styles for centered pages
  const pageContainerStyles = {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    padding: '2rem 1rem',
    boxSizing: 'border-box'
  };

  const authenticatedContainerStyles = {
    ...pageContainerStyles,
    paddingTop: '5rem' // More padding to avoid being hidden under fixed header
  };

  if (!user) {
    return (
      <div style={pageContainerStyles}>
        <Login onLoginSuccess={setUser} />
      </div>
    );
  }

  // Force profile creation if logged in but no profile
  if (user && profileCheckDone && !userProfile) {
    return (
      <div style={pageContainerStyles}>
        <CompleteProfile user={user} onProfileComplete={handleProfileComplete} />
      </div>
    );
  }

  return (
    <BrowserRouter>
      <div className="app-main">
        <UserHeader user={user} userProfile={userProfile} onSignOut={handleSignOut} />
        <div style={authenticatedContainerStyles}>
          <Routes>
            <Route path="/" element={<LandingPage user={user} userProfile={userProfile} onSignOut={handleSignOut} />} />
            <Route path="/party/:partyId" element={<SignupSheet user={user} userProfile={userProfile} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>

        <button
          className="feedback-fab"
          onClick={() => setIsFeedbackOpen(true)}
          title="Give Feedback"
        >
          🚀
        </button>

        <FeedbackModal
          isOpen={isFeedbackOpen}
          onClose={() => setIsFeedbackOpen(false)}
          user={user}
          userProfile={userProfile}
        />
      </div>
    </BrowserRouter>
  );
}

export default App;
